import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { ImageData } from './cardAttributeModelIndex';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type BookDb = {
  name: string;
  notes: string;
  coverImage?: ImageData;
  goodsId?: string;
  version?: number;
}
const initial: BookDb = {
  name: '',
  notes: '',
  coverImage: undefined,
  goodsId: undefined,
  version: undefined
};

export type BookDbHashType = {
  [key in string]: BookDb;
}

export type BookAddPayload = {
  id?: string;
} & BookDb;
export type BookUpdatePayload = {
  id: string;
} & { [P in keyof BookDb]?: BookDb[P] };

export type BookDbState = {
  books: BookDbHashType;
  currentBookId: string | null;
}

export const generateBookId = (): string => generateId('b');
let initialState: BookDbState = { books: {}, currentBookId: null };
const key = 'books';

const bookDbSlice = createSlice({
  name: 'bookDb',
  initialState,
  reducers: {
    initializeBooks(state: BookDbState) {
      state.books = {};
    },
    addBook(state: BookDbState, action: PayloadAction<BookAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateBookId();
      state.books[id] = obj;
      saveData(key, id, obj);
    },
    updateBook(state: BookDbState, action: PayloadAction<BookUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.books[id] === undefined) {
        console.log(`*** Book not found: ${id}`);
      } else {
        const newData = { ...state.books[id], ...obj };
        state.books[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteBook(state: BookDbState, action: PayloadAction<BookUpdatePayload>) {
      const id = action.payload.id;
      delete state.books[id];
      deleteData(key, id);
    },
    saveBooks(state: BookDbState) {
      saveAllData(key, state.books)
    },
    setCurrentBook(state: BookDbState, action: PayloadAction<string | null>) {
      state.currentBookId = action.payload;
    },
    signUpBooks(state: BookDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.books, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: BookDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.books = updatedValues;
      if (changed) saveAllData(key, state.books);
    })
  }
})

export const { initializeBooks, addBook, updateBook, deleteBook, saveBooks, setCurrentBook, signUpBooks } = bookDbSlice.actions;
export default bookDbSlice.reducer;
